import { Box, Heading, Icon, Text } from '@medely/ui-kit';
import React from 'react';
import { JobDetailsListItem } from '../jobs';
import { FragmentType, parseFragmentData } from '@mp/graphql/generated';
import { AssignmentForAssignmentDetailsGql } from './graphql';

type AssignmentClinicalInstructionsProps = {
  assignment?: FragmentType<typeof AssignmentForAssignmentDetailsGql> | null;
  smallIcon?: boolean;
};

export const AssignmentClinicalInstructions = ({
  assignment: inputAssignment,
  smallIcon,
}: AssignmentClinicalInstructionsProps) => {
  const assignment = parseFragmentData(AssignmentForAssignmentDetailsGql, inputAssignment);

  const bookedOrInProgress = ['booked', 'in_progress'].includes(assignment?.status ?? '');

  const ehrDetails = assignment?.primary_location?.ehr_details;
  const ehrName = assignment?.ehr_system?.label;
  const hasEhrData = ehrDetails || ehrName;

  const medicationManagementDetails = assignment?.primary_location?.medication_management_details;

  const suppliesAndPPEDetails = assignment?.primary_location?.supplies_ppe_details;

  const hasData = () => {
    if (
      hasEhrData ||
      (medicationManagementDetails && bookedOrInProgress) ||
      (suppliesAndPPEDetails && bookedOrInProgress)
    ) {
      return true;
    }
    return false;
  };

  if (!hasData()) {
    return null;
  }

  return (
    <Box testId="assignment-clinical-instructions" pb={2}>
      <Box py={2}>
        <Heading size="s" color="text.primary" testId="assignment-clinical-instructions-title">
          Clinical instructions
        </Heading>
      </Box>
      {hasEhrData && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-clipboard-medical"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Clinical charting"
          >
            {ehrName && <Text size="m" color="text.secondary">{`• EHR: ${ehrName}`}</Text>}
            {ehrDetails && bookedOrInProgress && (
              <Text size="m" color="text.secondary">{`• ${ehrDetails}`}</Text>
            )}
          </JobDetailsListItem>
        </Box>
      )}
      {medicationManagementDetails && bookedOrInProgress && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-prescription"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Medication management"
          >
            <Text size="m" color="text.secondary">
              {medicationManagementDetails}
            </Text>
          </JobDetailsListItem>
        </Box>
      )}
      {suppliesAndPPEDetails && bookedOrInProgress && (
        <Box pt={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-stethoscope"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Supplies and PPE"
          >
            <Text size="m" color="text.secondary">
              {suppliesAndPPEDetails}
            </Text>
          </JobDetailsListItem>
        </Box>
      )}
    </Box>
  );
};
